/**
 * Adversarial Reclassification API — Carrier Counter-Model workbench clients.
 *
 * Contract source of truth: docs/plans/2026-07-30-adversarial-reclassification-seam.md §6.
 * Four endpoints, all under /api/v1/accountant-analysis/{report_id}:
 *   1. POST .../adversarial-reclassification            — recompute with the carrier's F/V/P map
 *   2. POST .../carrier-counter-model                   — persist scenario + rebuttals
 *   3. GET  .../carrier-counter-model                   — load the persisted scenario
 *   4. POST .../carrier-counter-model/defense-strategy  — suggested defense for one attack vector
 *
 * METHODOLOGY NOTE (correct framing — seam doc §0): the adversarial lever is
 * variable-profit-margin reclassification via
 * `bi_engine.calculate_monthly_variable_profit`. Flipping a category to "V"
 * moves its dollars into `variable_costs` inside the VP margin, which shrinks
 * coverage-period variable profit and therefore the final claim. It is NOT a
 * "saved-cost deduction" — SavedCostsCalculator's bucket totals are
 * classification-invariant and never move.
 */

import axios from '@/lib/http';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// =============================================================================
// TYPES — mirror the seam-map §6 contract verbatim
// =============================================================================

export type AuthHeader = { Authorization: string } | {};

/** Carrier-side classification codes accepted by the contract. */
export type ExpenseClass = 'F' | 'V' | 'P';

/**
 * DefenseMatrixGenerator-shaped rebuttal strategy (seam §4b). All fields
 * optional because the backend may return a bare string or omit the block.
 */
export interface DefenseStrategy {
  label?: 'A' | 'B' | 'C';
  name?: string;
  approach?: string;
  evidence_needed?: string[];
  counter_arguments?: string[];
}

export interface AttackVector {
  category: string;
  /** Engine-truth class from the canonical map. May be "SV" in engine data even
   *  though the contract narrows to F|V|P — kept wide here so a real response
   *  never fails the type. */
  our_class: ExpenseClass | 'SV' | string;
  their_class: ExpenseClass;
  our_amount: number;
  their_amount: number;
  /** Isolated single-category recompute delta (negative = strips our claim). */
  loss_impact: number;
  /** Free text for v1 — the carrier accountant's stated rationale. */
  methodology?: string | null;
  rebuttal?: DefenseStrategy | null;
}

export interface AdversarialReclassificationResponse {
  report_id: string;
  our_net_loss: number;
  their_net_loss: number;
  /** their_net_loss - our_net_loss (negative in the classic carrier-favorable case). */
  delta: number;
  /** False when Σ loss_impact ≠ delta (claim-floor interaction) — surfaced, never hidden. */
  reconciles: boolean;
  /** Ranked by abs(loss_impact) desc by the BACKEND — the frontend never re-sorts. */
  attack_vectors: AttackVector[];
}

export type VectorStatus = 'countered' | 'partial' | 'exposed';

export interface CounterModelVector {
  category: string;
  rebuttal_text: string;
  status: VectorStatus;
}

export interface CarrierCounterModelSave {
  classification_overrides: Record<string, ExpenseClass>;
  vectors: CounterModelVector[];
  label?: string;
}

/** Shape returned by GET .../carrier-counter-model (the persisted document). */
export interface CarrierCounterModelDoc extends CarrierCounterModelSave {
  report_id?: string;
  saved_at?: string;
  updated_at?: string;
}

export interface DefenseStrategyRequest {
  category: string;
  their_class: ExpenseClass;
}

/**
 * Response shape for the defense-strategy endpoint. The seam doc specifies the
 * request body but not the response envelope, so this type is deliberately
 * tolerant: strategy (single), strategies (list), or a bare suggestion string.
 */
export interface DefenseStrategyResponse {
  category?: string;
  their_class?: ExpenseClass;
  strategy?: DefenseStrategy | string;
  strategies?: DefenseStrategy[];
  suggestion?: string;
}

// =============================================================================
// CLIENTS
// =============================================================================

const base = (reportId: string) =>
  `${BACKEND_URL}/api/v1/accountant-analysis/${reportId}`;

/**
 * Recompute the claim under the carrier's classification map.
 * `classification_overrides` is SPARSE — only categories that differ from ours.
 */
export async function computeAdversarialReclassification(
  reportId: string,
  body: {
    classification_overrides: Record<string, ExpenseClass>;
    expense_overrides?: Record<string, number>;
  },
  authHeader: AuthHeader,
): Promise<AdversarialReclassificationResponse> {
  const res = await axios.post(`${base(reportId)}/adversarial-reclassification`, body, {
    headers: authHeader,
  });
  return res.data as AdversarialReclassificationResponse;
}

/** Persist the scenario + the accountant's rebuttal work product. */
export async function saveCarrierCounterModel(
  reportId: string,
  body: CarrierCounterModelSave,
  authHeader: AuthHeader,
): Promise<{ status?: string; saved_at?: string }> {
  const res = await axios.post(`${base(reportId)}/carrier-counter-model`, body, {
    headers: authHeader,
  });
  return res.data ?? {};
}

/**
 * Load the persisted scenario. Returns null when none exists yet (404, or an
 * empty document — the harness/default mock returns `{}` for unmatched routes).
 */
export async function loadCarrierCounterModel(
  reportId: string,
  authHeader: AuthHeader,
): Promise<CarrierCounterModelDoc | null> {
  try {
    const res = await axios.get(`${base(reportId)}/carrier-counter-model`, {
      headers: authHeader,
    });
    // Backend returns a WRAPPER { report_id, count, models, latest } — the saved
    // scenario is under `latest` (models[-1]), not at the top level. Reading the
    // top level always yielded null, so saved witness work never reloaded
    // (schema review 2026-08-11). Backend stamps `created_at`; map it to saved_at.
    const wrapper = res.data as { latest?: (CarrierCounterModelDoc & { created_at?: string }) | null } | null;
    const doc = wrapper && typeof wrapper === 'object' ? wrapper.latest : null;
    if (!doc || typeof doc !== 'object') return null;
    if (!doc.classification_overrides || Object.keys(doc.classification_overrides).length === 0) {
      return null; // empty/placeholder document = no saved model
    }
    return { ...doc, saved_at: doc.saved_at ?? doc.created_at };
  } catch (err: any) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
}

/** Ask the backend (DefenseMatrixGenerator seam) for a suggested defense. */
export async function suggestDefenseStrategy(
  reportId: string,
  body: DefenseStrategyRequest,
  authHeader: AuthHeader,
): Promise<DefenseStrategyResponse> {
  const res = await axios.post(`${base(reportId)}/carrier-counter-model/defense-strategy`, body, {
    headers: authHeader,
  });
  return (res.data ?? {}) as DefenseStrategyResponse;
}

// =============================================================================
// v1.1 — Dimension #1: Saved-costs offset attack
// Contract: docs/plans/2026-07-31-carrier-counter-model-v1.1-seam.md §1.
// A SEPARATE lever from reclassification: net loss = gross − saved, and the
// carrier enlarges `saved` by asserting the business's actual loss-period
// expenses were LOWER than our records show. `SavedCostsCalculator.total_saved`
// is classification-invariant, so this dimension is independent of the F/V/P map.
// =============================================================================

export interface SavedCostsRow {
  category: string;
  classification: string;
  projected: number;
  actual: number;
  saved: number;
}

export interface SavedCostsAttackResponse {
  report_id: string;
  our_total_saved: number;
  their_total_saved: number;
  /** their − our: the EXTRA offset the carrier would subtract from the claim. */
  delta: number;
  our_detail: SavedCostsRow[] | null;
  their_detail: SavedCostsRow[] | null;
}

/**
 * Compute the saved-costs offset our way vs the carrier's. `carrierActualExpenses`
 * is the witness's drop-in of the carrier's asserted actual loss-period expenses,
 * keyed by expense category / P&L bucket (sparse — unspecified lines hold at ours).
 */
export async function computeSavedCostsAttack(
  reportId: string,
  carrierActualExpenses: Record<string, number> | null,
  authHeader: AuthHeader,
): Promise<SavedCostsAttackResponse> {
  const res = await axios.post(
    `${base(reportId)}/saved-costs-attack`,
    { carrier_actual_expenses: carrierActualExpenses ?? undefined },
    { headers: authHeader },
  );
  return res.data as SavedCostsAttackResponse;
}

// =============================================================================
// v1.1 — Dimension #4: Policy-deduction waterfall
// Contract: docs/plans/2026-07-31-carrier-counter-model-v1.1-seam.md §4.
// The carrier applies/maximizes deductible, coinsurance penalty, and policy
// limits to cut the net payout below the proven gross loss. The witness drops in
// the carrier's asserted policy terms; the SAME waterfall runs on our gross both
// ways. Every figure is computed by the backend policy-deductions engine.
// =============================================================================

export interface WaterfallStep {
  step: number | null;
  description: string | null;
  deduction_amount: number;
  running_balance: number;
}

export interface PolicyWaterfallAttackResponse {
  report_id: string;
  gross_loss: number;
  our_net_claim: number;
  their_net_claim: number;
  /** their − our: ≤ 0 when the carrier's terms cut more than our filed terms. */
  delta: number;
  our_waterfall: WaterfallStep[];
  their_waterfall: WaterfallStep[] | null;
  our_policy_terms: Record<string, number>;
  carrier_policy_terms: Record<string, number> | null;
}

/** Which gross loss the waterfall acts on: the standard-BI claim, the first-party
 *  property RCV total, or the forced-closure total demand. */
export type WaterfallGrossBasis = 'bi' | 'property' | 'fc';

/**
 * Run the policy-deduction waterfall our filed terms vs the carrier's. Recognized
 * carrier fields: dollar_deductible, coinsurance_pct, actual_coverage_limit,
 * policy_limit, adv_deductible_days, waiting_period_hours, annual_bi_value.
 * `grossBasis` selects the gross ('bi' default, or 'property' for the RCV total).
 */
export async function computePolicyWaterfallAttack(
  reportId: string,
  carrierPolicyTerms: Record<string, number> | null,
  authHeader: AuthHeader,
  grossBasis: WaterfallGrossBasis = 'bi',
): Promise<PolicyWaterfallAttackResponse> {
  const res = await axios.post(
    `${base(reportId)}/policy-waterfall-attack`,
    { carrier_policy_terms: carrierPolicyTerms ?? undefined, gross_basis: grossBasis },
    { headers: authHeader },
  );
  return res.data as PolicyWaterfallAttackResponse;
}

// =============================================================================
// v1.1 — Dimension #2: Non-damage factor separation (insured %)
// Contract: docs/plans/2026-07-31-carrier-counter-model-v1.1-seam.md §2.
// The carrier argues a pre-existing declining revenue trend caused part of the
// shortfall, so only insured_percentage of the proven claim is compensable. The
// engine fits an OLS trend over baseline-year revenue; the carrier's lever is the
// baseline WINDOW. Every percentage comes from the backend engine.
// =============================================================================

export interface InsuredPercentageAttackResponse {
  report_id: string;
  gross_claim: number;
  available_baseline_years: number[];
  our_baseline_years: number[];
  our_insured_pct: number;
  their_insured_pct: number;
  our_compensable: number;
  their_compensable: number;
  /** their − our: ≤ 0 when the carrier's narrower baseline strips compensable loss. */
  delta: number;
  carrier_baseline_years: number[] | null;
  our_factors: string[];
  their_factors: string[] | null;
}

/**
 * Recompute insured % our full baseline vs the carrier's chosen baseline window.
 * `carrierBaselineYears` is the witness's drop-in — a subset of the case's
 * available baseline years (cherry-picking declining years lowers insured %).
 */
export async function computeInsuredPercentageAttack(
  reportId: string,
  carrierBaselineYears: number[] | null,
  authHeader: AuthHeader,
): Promise<InsuredPercentageAttackResponse> {
  const res = await axios.post(
    `${base(reportId)}/insured-percentage-attack`,
    { carrier_baseline_years: carrierBaselineYears ?? undefined },
    { headers: authHeader },
  );
  return res.data as InsuredPercentageAttackResponse;
}

// =============================================================================
// v1.1 — Dimension #3: Benchmark selection
// Contract: docs/plans/2026-07-31-carrier-counter-model-v1.1-seam.md §3.
// calculate_claim picks the compensation-maximizing benchmark window; the carrier
// proposes a less-favorable window, lowering the claim. Every claim is the real
// BICalculator output (the carrier's via a forced-benchmark recompute).
// =============================================================================

export interface BenchmarkCandidate {
  id: string;
  period_type: string;
  label: string;
  variable_profit: number;
  revenue: number;
  variance: number;
}

export interface BenchmarkAttackResponse {
  report_id: string;
  our_benchmark_id: string;
  our_benchmark_label: string;
  our_claim: number;
  their_benchmark_id: string | null;
  their_benchmark_label: string | null;
  their_claim: number;
  /** their − our: ≤ 0 when the carrier's window is less favorable. */
  delta: number;
  total_candidates: number;
  candidates: BenchmarkCandidate[];
}

/**
 * Our compensation-maximizing benchmark vs the carrier's proposed window.
 * `carrierBenchmarkId` is the witness's drop-in — a candidate id from the
 * response's ranked list (e.g. "3-Month|Jul 2022|Sep 2022").
 */
export async function computeBenchmarkAttack(
  reportId: string,
  carrierBenchmarkId: string | null,
  authHeader: AuthHeader,
): Promise<BenchmarkAttackResponse> {
  const res = await axios.post(
    `${base(reportId)}/benchmark-attack`,
    { carrier_benchmark_id: carrierBenchmarkId ?? undefined },
    { headers: authHeader },
  );
  return res.data as BenchmarkAttackResponse;
}

/** Normalize the tolerant defense-strategy response into a display list. */
export function normalizeDefenseStrategies(resp: DefenseStrategyResponse): DefenseStrategy[] {
  if (Array.isArray(resp.strategies) && resp.strategies.length > 0) return resp.strategies;
  if (resp.strategy) {
    return typeof resp.strategy === 'string' ? [{ approach: resp.strategy }] : [resp.strategy];
  }
  if (resp.suggestion) return [{ approach: resp.suggestion }];
  return [];
}
